import React, { useEffect, useRef, useState } from "react";
import { isPrerendering } from "../../utils/isPrerendering";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

const FLEET = [
  {
    id: "abb",
    label: "ABB",
    color: "#E30613",
    w: 36,
    h: 14,
    speed: 28,
    start: 0.12,
    dir: 1,
    rest: { x: 150, y: 208, angle: 0 },
  },
  {
    id: "bluepath",
    label: "BluePath",
    color: "#5B8DEF",
    w: 28,
    h: 12,
    speed: 24,
    start: 0.08,
    dir: 1,
    rest: { x: 320, y: 100, angle: 90 },
  },
  {
    id: "visionnav",
    label: "VisionNav",
    color: "#D4A017",
    w: 42,
    h: 12,
    speed: 22,
    start: 0.18,
    dir: 1,
    rest: { x: 160, y: 300, angle: 0 },
  },
];

const BOXES = [
  { id: "north", x0: 286, x1: 354, y0: 176, y1: 240 },
  { id: "south", x0: 286, x1: 354, y0: 272, y1: 328 },
];

function inBox(pose, box) {
  return pose.x >= box.x0 && pose.x <= box.x1 && pose.y >= box.y0 && pose.y <= box.y1;
}

function boxAt(pose) {
  return BOXES.find((box) => inBox(pose, box)) ?? null;
}

function poseAt(path, len, s, dir) {
  if (!path) return null;
  const t = Math.min(len, Math.max(0, s));
  const t2 = Math.min(len, Math.max(0, s + 6 * dir));
  const p0 = path.getPointAtLength(t);
  const p1 = path.getPointAtLength(t2);
  return {
    x: p0.x,
    y: p0.y,
    angle: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI,
  };
}

function advance(progress, dir, speed, dt, len) {
  let next = progress + speed * dir * dt;
  let nextDir = dir;
  if (next >= len) {
    next = len;
    nextDir = -1;
  } else if (next <= 0) {
    next = 0;
    nextDir = 1;
  }
  return { progress: next, dir: nextDir };
}

const UID = "plant";

export default function PlantSchematic() {
  const reduceMotion = usePrefersReducedMotion();
  const uid = UID;
  const pathRefs = useRef({});
  const [poses, setPoses] = useState(() =>
    Object.fromEntries(FLEET.map((unit) => [unit.id, { ...unit.rest, held: false }]))
  );

  useEffect(() => {
    if (reduceMotion || isPrerendering()) return undefined;
    if (FLEET.some((unit) => !pathRefs.current[unit.id])) return undefined;

    const lengths = FLEET.map((unit) => pathRefs.current[unit.id].getTotalLength());
    const progress = FLEET.map((unit, i) => unit.start * lengths[i]);
    const dirs = FLEET.map((unit) => unit.dir);
    let last = performance.now();
    let rafId = 0;
    let cancelled = false;

    const loop = (now) => {
      if (cancelled) return;
      const paths = FLEET.map((unit) => pathRefs.current[unit.id]);
      if (paths.some((path) => !path)) return;

      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const currents = FLEET.map((unit, i) => {
        const pose = poseAt(paths[i], lengths[i], progress[i], dirs[i]);
        return pose ? { ...pose, progress: progress[i] } : null;
      });
      if (currents.some((pose) => !pose)) return;

      const stepped = FLEET.map((unit, i) => {
        const moved = advance(progress[i], dirs[i], unit.speed, dt, lengths[i]);
        const pose = poseAt(paths[i], lengths[i], moved.progress, moved.dir);
        return pose ? { ...moved, pose } : null;
      });
      if (stepped.some((step) => !step)) return;

      const occupantOf = (box, except) =>
        currents.findIndex((pose, j) => j !== except && boxAt(pose)?.id === box.id);

      const entering = stepped.map((step, i) => {
        const nowBox = boxAt(currents[i]);
        const nextBox = boxAt(step.pose);
        return !nowBox && nextBox;
      });

      const accepted = currents.map((pose) => ({ ...pose, held: true, dir: null }));

      FLEET.forEach((_, i) => {
        if (boxAt(currents[i])) {
          accepted[i] = { ...stepped[i].pose, progress: stepped[i].progress, held: false, dir: stepped[i].dir };
        }
      });

      FLEET.forEach((_, i) => {
        if (accepted[i].dir !== null) return;
        const nextBox = boxAt(stepped[i].pose);
        if (!nextBox) {
          accepted[i] = { ...stepped[i].pose, progress: stepped[i].progress, held: false, dir: stepped[i].dir };
          return;
        }

        const stillOccupied =
          occupantOf(nextBox, i) !== -1 ||
          accepted.some((pose, j) => j !== i && pose.dir !== null && !pose.held && boxAt(pose)?.id === nextBox.id);

        const rivalEntering = entering.findIndex((isEntering, j) => {
          if (!isEntering || j === i) return false;
          const rivalBox = boxAt(stepped[j].pose);
          return rivalBox?.id === nextBox.id && FLEET[j].id !== FLEET[i].id;
        });
        const loseTie =
          rivalEntering !== -1 &&
          accepted[rivalEntering].dir === null &&
          FLEET[i].id > FLEET[rivalEntering].id;

        if (stillOccupied || loseTie) {
          accepted[i] = { ...currents[i], held: true, dir: dirs[i], progress: progress[i] };
          return;
        }

        accepted[i] = { ...stepped[i].pose, progress: stepped[i].progress, held: false, dir: stepped[i].dir };
      });

      accepted.forEach((pose, i) => {
        progress[i] = pose.progress;
        if (pose.dir !== null) dirs[i] = pose.dir;
      });

      setPoses(
        Object.fromEntries(
          FLEET.map((unit, i) => [
            unit.id,
            { x: accepted[i].x, y: accepted[i].y, angle: accepted[i].angle, held: accepted[i].held },
          ])
        )
      );

      if (!cancelled) rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [reduceMotion, uid]);

  const route = {
    abb: `${uid}-abb`,
    bluepath: `${uid}-bluepath`,
    visionnav: `${uid}-visionnav`,
  };

  return (
    <aside
      className="relative w-full overflow-hidden border border-gray-800 bg-white"
      aria-label="Plant blueprint. ABB and VisionNav run east-west; BluePath runs north-south. At crossings, one unit holds until the other clears."
    >
      <svg
        viewBox="0 0 640 400"
        className="h-auto w-full"
        aria-hidden="true"
      >
        <defs>
          <pattern id={`${uid}-hatch`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke="#6b7280" strokeWidth="0.7" />
          </pattern>
          <pattern id={`${uid}-hatch-tight`} width="3.5" height="3.5" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
            <line x1="0" y1="0" x2="0" y2="3.5" stroke="#9ca3af" strokeWidth="0.65" />
          </pattern>
        </defs>

        <rect width="640" height="400" fill="#ffffff" />

        <g stroke="#e5e7eb" strokeWidth="0.5">
          {Array.from({ length: 31 }).map((_, i) => (
            <line key={`vm-${i}`} x1={20 * (i + 1)} y1="0" x2={20 * (i + 1)} y2="400" />
          ))}
          {Array.from({ length: 19 }).map((_, i) => (
            <line key={`hm-${i}`} x1="0" y1={20 * (i + 1)} x2="640" y2={20 * (i + 1)} />
          ))}
        </g>
        <g stroke="#d1d5db" strokeWidth="0.8">
          {Array.from({ length: 15 }).map((_, i) => (
            <line key={`v-${i}`} x1={40 * (i + 1)} y1="0" x2={40 * (i + 1)} y2="400" />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`h-${i}`} x1="0" y1={40 * (i + 1)} x2="640" y2={40 * (i + 1)} />
          ))}
        </g>

        <rect x="8" y="8" width="624" height="384" fill="none" stroke="#374151" strokeWidth="1.2" />
        <rect x="12" y="12" width="616" height="376" fill="none" stroke="#9ca3af" strokeWidth="0.7" />

        {["1", "2", "3", "4", "5", "6", "7", "8"].map((mark, i) => (
          <text key={`col-${mark}`} x={80 + i * 64} y="24" fill="#4b5563" fontSize="7" letterSpacing="0.8" textAnchor="middle">
            {mark}
          </text>
        ))}
        {["A", "B", "C", "D", "E", "F"].map((mark, i) => (
          <text key={`row-${mark}`} x="22" y={56 + i * 52} fill="#4b5563" fontSize="7" letterSpacing="0.8">
            {mark}
          </text>
        ))}

        <rect x="20" y="40" width="56" height="316" fill="#f3f4f6" stroke="#4b5563" strokeWidth="1" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={`st-${i}`}>
            <rect x="26" y={52 + i * 58} width="44" height="46" fill="none" stroke="#4b5563" strokeWidth="0.8" />
            <line x1="26" y1={75 + i * 58} x2="70" y2={75 + i * 58} stroke="#9ca3af" strokeWidth="0.6" />
            <text x="48" y={70 + i * 58} fill="#111827" fontSize="6" letterSpacing="0.6" textAnchor="middle">
              {`ST-${String(i + 1).padStart(2, "0")}`}
            </text>
          </g>
        ))}
        <text x="48" y="368" fill="#111827" fontSize="7" letterSpacing="1.6" textAnchor="middle">
          LINE
        </text>

        <rect x="92" y="186" width="528" height="44" fill="#e5e7eb" stroke="#6b7280" strokeWidth="0.9" />
        <rect x="92" y="278" width="428" height="44" fill="#e5e7eb" stroke="#6b7280" strokeWidth="0.9" />
        <rect x="300" y="56" width="40" height="300" fill="#e5e7eb" stroke="#6b7280" strokeWidth="0.9" />

        <g fill={`url(#${uid}-hatch)`} stroke="#4b5563" strokeWidth="0.85">
          <rect x="380" y="44" width="86" height="118" />
          <rect x="474" y="44" width="86" height="118" />
        </g>
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={`rack-n-${i}`} stroke="#6b7280" strokeWidth="0.55">
            <line x1="386" y1={56 + i * 20} x2="460" y2={56 + i * 20} />
            <line x1="480" y1={56 + i * 20} x2="554" y2={56 + i * 20} />
          </g>
        ))}
        <text x="423" y="58" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          RACK A
        </text>
        <text x="517" y="58" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          RACK B
        </text>

        <g fill={`url(#${uid}-hatch-tight)`} stroke="#4b5563" strokeWidth="0.85">
          <rect x="540" y="278" width="80" height="78" />
        </g>
        {[0, 1, 2].map((i) => (
          <line key={`rack-s-${i}`} x1="548" y1={292 + i * 18} x2="612" y2={292 + i * 18} stroke="#6b7280" strokeWidth="0.55" />
        ))}
        <text x="580" y="270" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          RACK C
        </text>

        <rect x="96" y="44" width="184" height="118" fill="none" stroke="#4b5563" strokeWidth="0.9" strokeDasharray="3 3" />
        {[0, 1, 2].map((i) => (
          <rect key={`stage-${i}`} x={108 + i * 56} y="72" width="44" height="64" fill="#ffffff" stroke="#4b5563" strokeWidth="0.75" />
        ))}
        <text x="188" y="62" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          STAGING
        </text>

        {[0, 1].map((i) => (
          <g key={`chg-${i}`}>
            <rect x={210 + i * 40} y="238" width="32" height="20" fill="#ffffff" stroke="#111827" strokeWidth="0.85" />
            <line x1={216 + i * 40} y1="243" x2={236 + i * 40} y2="253" stroke="#4b5563" strokeWidth="0.7" />
            <line x1={236 + i * 40} y1="243" x2={216 + i * 40} y2="253" stroke="#4b5563" strokeWidth="0.7" />
          </g>
        ))}
        <text x="246" y="234" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          CHARGE
        </text>

        <g stroke="#111827" strokeWidth="1">
          <line x1="620" y1="186" x2="620" y2="230" />
          <path d="M 620 190 A 14 14 0 0 1 620 218" fill="none" />
          <line x1="620" y1="278" x2="620" y2="322" />
          <path d="M 620 282 A 14 14 0 0 1 620 310" fill="none" />
        </g>
        <text x="628" y="212" fill="#111827" fontSize="6" letterSpacing="1.1" transform="rotate(90 628 212)">
          DOCK
        </text>

        {[
          [160, 168],
          [240, 168],
          [400, 168],
          [480, 168],
          [160, 260],
          [240, 260],
          [400, 260],
          [160, 348],
          [240, 348],
          [400, 348],
        ].map(([x, y]) => (
          <g key={`col-${x}-${y}`}>
            <circle cx={x} cy={y} r="3.2" fill="#ffffff" stroke="#111827" strokeWidth="0.9" />
            <circle cx={x} cy={y} r="1.1" fill="#4b5563" />
          </g>
        ))}

        <g stroke="#4b5563" strokeWidth="0.9" strokeDasharray="3 5">
          <line x1="92" y1="208" x2="620" y2="208" />
          <line x1="92" y1="300" x2="520" y2="300" />
          <line x1="320" y1="56" x2="320" y2="356" />
        </g>

        <g fill="none" stroke="#111827" strokeWidth="1.1">
          <rect x="286" y="186" width="68" height="44" />
          <rect x="286" y="278" width="68" height="44" />
        </g>
        <text x="320" y="178" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          XING N
        </text>
        <text x="320" y="338" fill="#111827" fontSize="7" letterSpacing="1.2" textAnchor="middle">
          XING S
        </text>

        <text x="108" y="178" fill="#111827" fontSize="7" letterSpacing="1.4">
          AISLE A  ·  E-W
        </text>
        <text x="108" y="270" fill="#111827" fontSize="7" letterSpacing="1.4">
          AISLE B  ·  E-W
        </text>
        <text
          x="348"
          y="210"
          fill="#111827"
          fontSize="7"
          letterSpacing="1.4"
          transform="rotate(-90 348 210)"
        >
          AISLE C  ·  N-S
        </text>

        <g transform="translate(88 52)">
          <line x1="0" y1="26" x2="0" y2="0" stroke="#111827" strokeWidth="1.05" />
          <polygon points="0,-2 -3.2,7 3.2,7" fill="#111827" />
          <text x="0" y="-7" fill="#111827" fontSize="8" fontWeight="600" textAnchor="middle">
            N
          </text>
        </g>

        <g transform="translate(92 372)" stroke="#111827" fill="#111827">
          <line x1="0" y1="0" x2="80" y2="0" strokeWidth="0.9" />
          <line x1="0" y1="-4" x2="0" y2="4" strokeWidth="0.9" />
          <line x1="40" y1="-2.5" x2="40" y2="2.5" strokeWidth="0.7" />
          <line x1="80" y1="-4" x2="80" y2="4" strokeWidth="0.9" />
          <text x="40" y="14" fontSize="7" letterSpacing="0.8" textAnchor="middle">
            NTS
          </text>
        </g>

        <path
          id={route.abb}
          ref={(el) => {
            pathRefs.current.abb = el;
          }}
          d="M 110 208 H 590"
          fill="none"
          stroke="#E30613"
          strokeOpacity="0.75"
          strokeWidth="1.35"
        />
        <path
          id={route.bluepath}
          ref={(el) => {
            pathRefs.current.bluepath = el;
          }}
          d="M 320 80 V 350"
          fill="none"
          stroke="#5B8DEF"
          strokeOpacity="0.75"
          strokeWidth="1.35"
        />
        <path
          id={route.visionnav}
          ref={(el) => {
            pathRefs.current.visionnav = el;
          }}
          d="M 110 300 H 500"
          fill="none"
          stroke="#D4A017"
          strokeOpacity="0.75"
          strokeWidth="1.35"
        />

        {FLEET.map((unit) => {
          const pose = poses[unit.id];
          return (
            <g
              key={unit.id}
              transform={`translate(${pose.x} ${pose.y}) rotate(${pose.angle})`}
              opacity={pose.held ? 0.4 : 1}
            >
              <rect x={-unit.w / 2} y={-unit.h / 2} width={unit.w} height={unit.h} fill={unit.color} />
            </g>
          );
        })}
      </svg>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-800 bg-white px-4 py-2.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-700">
          Key
        </span>
        {FLEET.map((unit) => (
          <span key={unit.id} className="flex items-center gap-2 text-xs font-medium text-gray-900">
            <span className="inline-block h-2 w-5 shrink-0" style={{ backgroundColor: unit.color }} />
            {unit.label}
          </span>
        ))}
        <span className="ml-auto text-[10px] uppercase tracking-[0.16em] text-gray-600">
          DWG-01 · plant floor · NTS
        </span>
      </div>
    </aside>
  );
}
